import type { GeminiImageModel, GeminiImageSize, TokenUsageEntry } from '../types'
import { GEMINI_ACTIVE_PRICING, type GeminiModelPricing } from './gemini-image-model-manifest'

export type { GeminiModelPricing } from './gemini-image-model-manifest'

/**
 * Per-model pricing for Gemini API usage.
 *
 * Cost estimation is computed on read (not persisted) from the append-only
 * `tokensUsed` arrays on retained Workflow and Playground records. Update rates here via
 * code change; confirm against https://ai.google.dev/gemini-api/docs/pricing
 * when rates change.
 */
/**
 * Prices for retired model IDs that can still appear in append-only usage
 * records. These IDs are not active model options and must not be moved into
 * the image-workflow manifest.
 */
export const GEMINI_HISTORICAL_PRICING: Readonly<Record<string, GeminiModelPricing>> = {
  'gemini-2.5-flash': {
    inputPricePer1M: 0.3,
    outputPricePer1M: 2.5
  },
  // Legacy Nano Banana image model retained for historical cost rows only.
  'gemini-2.5-flash-image': {
    inputPricePer1M: 0.3,
    outputPricePer1M: 2.5,
    imageOutputPrice: 0.039
  },
  // @NOTE: Backwards compatible
  'gemini-3-pro-image-preview': {
    inputPricePer1M: 2,
    outputPricePer1M: 12,
    imageOutputPrice: 0.134,
    imageOutputPriceBySize: {
      '1K': 0.134,
      '2K': 0.134,
      '4K': 0.24
    }
  },
  // @NOTE: Backwards compatible
  'gemini-3.1-flash-image-preview': {
    inputPricePer1M: 0.5,
    outputPricePer1M: 3,
    imageOutputPrice: 0.101,
    imageOutputPriceBySize: {
      '1K': 0.067,
      '2K': 0.101,
      '4K': 0.151
    }
  }
}

/** Current catalog prices plus immutable rates for retained historical usage. */
export const GEMINI_PRICING: Readonly<Record<string, GeminiModelPricing>> = {
  ...GEMINI_HISTORICAL_PRICING,
  ...GEMINI_ACTIVE_PRICING
}

/** Image-output portion of a current model's estimate, before input or thinking tokens. */
export function estimateImageOutputCostUsd(model: GeminiImageModel, imageSize: GeminiImageSize, count = 1): number {
  const pricing = GEMINI_ACTIVE_PRICING[model]
  return (pricing.imageOutputPriceBySize?.[imageSize] ?? pricing.imageOutputPrice ?? 0) * count
}

// Types of token entries that represent a generated image. Image output is
// billed by its dedicated image SKU; separately reported thinking is billed at
// the model's text-output rate.
const IMAGE_OUTPUT_TYPES = new Set([
  'character',
  'background',
  'inpaint',
  'remove-background',
  'composition',
  'outpaint',
  'upscale',
  'freeflow'
])

/**
 * Compute total estimated cost (USD) for a list of token usage entries.
 *
 * Pure function: safe to call from frontend and backend. Unknown models
 * contribute $0 (logged upstream); this prevents NaN from leaking into UI.
 */
export function estimateCostUsd(tokensUsed: TokenUsageEntry[] | undefined): number {
  if (!tokensUsed || tokensUsed.length === 0) return 0

  let total = 0
  for (const entry of tokensUsed) {
    const pricing = GEMINI_PRICING[entry.model]
    if (!pricing) continue

    total += (entry.inputTokens / 1_000_000) * pricing.inputPricePer1M
    total += ((entry.googleSearchQueryCount ?? 0) / 1_000) * (pricing.googleSearchPricePer1KQueries ?? 0)
    const hasImageOutputPricing = pricing.imageOutputPrice !== undefined || pricing.imageOutputPriceBySize !== undefined
    if (IMAGE_OUTPUT_TYPES.has(entry.type) && hasImageOutputPricing) {
      total +=
        (entry.imageSize ? pricing.imageOutputPriceBySize?.[entry.imageSize] : undefined) ??
        pricing.imageOutputPrice ??
        0
      total += ((entry.thinkingTokens ?? 0) / 1_000_000) * pricing.outputPricePer1M
    } else {
      total += (entry.outputTokens / 1_000_000) * pricing.outputPricePer1M
    }
  }
  return total
}

/**
 * Sum token counts across a list of token usage entries.
 * Useful for displaying a single aggregate number in the UI.
 */
export function sumTokens(tokensUsed: TokenUsageEntry[] | undefined): number {
  if (!tokensUsed || tokensUsed.length === 0) return 0
  return tokensUsed.reduce((acc, entry) => acc + entry.totalTokens, 0)
}
